import React, { useRef, useEffect, useState } from 'react';
import * as faceapi from 'face-api.js';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Modal,
  Backdrop,
  Fade,
  Divider,
  List,
  ListItem,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TopNav from './Navbar';
import LeftNavbar from './LeftNav';
import Breadcrumb from './BreadCrump';

const AutomaticExam = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [referenceImage, setReferenceImage] = useState(null);
  const [referenceDescriptor, setReferenceDescriptor] = useState(null);
  const [matchResult, setMatchResult] = useState('');
  const location = useLocation();
  const examDetails = location.state?.exam;
  const [openModal, setOpenModal] = useState(true);
  const handleClose = () => setOpenModal(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [answers, setAnswers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const examId = examDetails?.id;
  const API_BASE = 'http://196.216.9.34:5020/api/exams';
  const token = localStorage.getItem('token');

  const parseDurationToSeconds = (hhmm) => {
    const [hours, minutes] = hhmm.split(':').map(Number);
    return (hours * 60 + minutes) * 60;
  };

  const [timeLeft, setTimeLeft] = useState(0);

useEffect(() => {
  if (examDetails?.duration && openModal) {
    const totalSeconds = parseDurationToSeconds(examDetails.duration);
    setTimeLeft(totalSeconds);

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleClose(); // Close modal
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer); // Clean up
  }
}, [examDetails?.duration, openModal]);

const formatTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

  

  const handleSubmitAnswers = async () => {
    if (!examDetails || !Object.keys(userAnswers).length) {
      alert("Please answer at least one question before submitting.");
      return;
    }
  
    try {
      const payload = {
        answers: Object.entries(userAnswers).map(([question_id, student_answer]) => ({
          question_id: parseInt(question_id),
          student_answer
        }))
      };
  
      const response = await fetch(`${API_BASE}/${examDetails.id}/attempts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` // Your stored JWT
        },
        body: JSON.stringify(payload),
      });
  
      if (!response.ok) {
        throw new Error('Failed to submit answers');
      }
  
      const result = await response.json();
      alert("Answers submitted successfully!");
      handleClose(); // Close modal
    } catch (error) {
      console.error(error);
      alert("An error occurred while submitting your answers.");
    }
  };

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!examId) return;

      try {
        const res = await fetch(`${API_BASE}/${examId}/questions`);
        if (!res.ok) throw new Error('Failed to fetch questions');
        const data = await res.json();
        setQuestions(data);
      } catch (err) {
        console.error('Error fetching questions:', err.message);
      }
    };

    if (openModal && examDetails) {
      fetchQuestions();
    }
  }, [openModal, examDetails, examId]);

  useEffect(() => {
    const loadModels = async () => {
      const MODEL_URL = process.env.PUBLIC_URL + '/models';
      await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
      await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
      await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
    };
    loadModels();
  }, []);

  const startVideo = () => {
    navigator.mediaDevices.getUserMedia({ video: {} })
      .then((stream) => {
        videoRef.current.srcObject = stream;
      })
      .catch((err) => console.error('Error starting webcam:', err));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const img = await faceapi.bufferToImage(file);
    setReferenceImage(img);

    const detection = await faceapi
      .detectSingleFace(img, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks()
      .withFaceDescriptor();

    if (detection) {
      setReferenceDescriptor(detection.descriptor);
      startVideo();
    } else {
      alert('No face detected in uploaded image.');
    }
  };

  const handleVideoOnPlay = () => {
    const canvas = canvasRef.current;
    const displaySize = {
      width: videoRef.current.videoWidth,
      height: videoRef.current.videoHeight,
    };

    faceapi.matchDimensions(canvas, displaySize);

    const interval = setInterval(async () => {
      const detections = await faceapi
        .detectAllFaces(videoRef.current, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptors();

      const resizedDetections = faceapi.resizeResults(detections, displaySize);
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      faceapi.draw.drawDetections(canvas, resizedDetections);

      if (referenceDescriptor && detections.length > 0) {
        const faceMatcher = new faceapi.FaceMatcher(referenceDescriptor, 0.6);
        const results = resizedDetections.map(d => faceMatcher.findBestMatch(d.descriptor));

        results.forEach((result, i) => {
          const box = resizedDetections[i].detection.box;
          const drawBox = new faceapi.draw.DrawBox(box, { label: result.toString() });
          drawBox.draw(canvas);
          setMatchResult(result.toString());
        });
      }
    }, 200);

    return () => clearInterval(interval);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <LeftNavbar />
      <Box sx={{ flexGrow: 1, ml: { lg: '240px' } }}>
        <TopNav />
        <Box sx={{ p: 10, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
          <Breadcrumb currentPage="Automatic Exam" />
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 4, color: '#ff6600' }}>
            <CameraAltIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Automatic Face Verification
          </Typography>

          <Card sx={{ p: 4, boxShadow: 3 }}>
            <CardContent>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={{ mb: 2 }}
              >
                Upload Reference Image
                <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
              </Button>

              {referenceImage && (
                <Box sx={{ mt: 2, mb: 3 }}>
                  <Typography variant="subtitle1">Reference Image:</Typography>
                  <img src={referenceImage.src} alt="Reference" style={{ width: 200, borderRadius: 8 }} />
                </Box>
              )}

              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  onPlay={handleVideoOnPlay}
                  style={{ width: 640, height: 480, borderRadius: 4 }}
                />
                <canvas
                  ref={canvasRef}
                  width={640}
                  height={480}
                  style={{ position: 'absolute', top: 0, left: 0, zIndex: 10 }}
                />
              </Box>

              {matchResult && (
                <Alert severity={matchResult.includes('unknown') ? 'error' : 'success'} sx={{ mt: 3 }}>
                  Match Result: {matchResult}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Exam Details Modal */}
      <Modal
  open={openModal}
  onClose={handleClose}
  closeAfterTransition
  BackdropComponent={Backdrop}
  BackdropProps={{ timeout: 500 }}
>
  <Fade in={openModal}>
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxHeight: '80vh',
        overflowY: 'auto',
        bgcolor: 'background.paper',
        borderRadius: 4,
        boxShadow: 24,
        p: 4,
      }}
    >
      <Typography variant="h5" fontWeight="bold" color="#ff6600" gutterBottom>
        📘 Exam Details
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Box
  sx={{
    mb: 2,
    p: 2,
    border: '2px dashed #1976d2',
    borderRadius: 2,
    backgroundColor: '#e3f2fd',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }}
>
  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
    ⏳ Time Remaining: {formatTime(timeLeft)}
  </Typography>
</Box>


      {examDetails ? (
        <>
          <Box mb={2}>
            <Typography><strong>Title:</strong> {examDetails.title}</Typography>
            <Typography><strong>Subject:</strong> {examDetails.subject}</Typography>
            <Typography><strong>Duration:</strong> {examDetails.duration}</Typography>
            <Typography><strong>Scheduled At:</strong> {new Date(examDetails.scheduledAt).toLocaleString()}</Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" color="primary" gutterBottom>
            ✏️ Questions
          </Typography>

          {questions.length > 0 ? (
            <List sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {questions.map((q, index) => (
                <Box
                  key={q.id}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid #e0e0e0',
                    p: 3,
                    backgroundColor: '#fafafa',
                    boxShadow: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Q{index + 1}: {q.question}
                  </Typography>

                  {q.type.trim().toLowerCase() === 'multiple choice' && q.options ? (
                    (() => {
                      let optionsArray = [];

                      if (typeof q.options === 'string') {
                        try {
                          const parsed = JSON.parse(q.options);
                          if (Array.isArray(parsed)) {
                            optionsArray = parsed;
                          } else {
                            optionsArray = q.options.split(',').map(opt => opt.trim());
                          }
                        } catch {
                          optionsArray = q.options.split(',').map(opt => opt.trim());
                        }
                      } else if (Array.isArray(q.options)) {
                        optionsArray = q.options;
                      }

                      return optionsArray.map((option, i) => (
                        <Box key={i} sx={{ mb: 1 }}>
                          <label style={{ display: 'flex', alignItems: 'center' }}>
                            <input
                              type="radio"
                              name={`question_${q.id}`}
                              value={option}
                              checked={userAnswers[q.id] === option}
                              onChange={(e) =>
                                setUserAnswers((prev) => ({
                                  ...prev,
                                  [q.id]: e.target.value,
                                }))
                              }
                              style={{ marginRight: 8 }}
                            />
                            <Typography variant="body2">{option}</Typography>
                          </label>
                        </Box>
                      ));
                    })()
                  ) : (
                    <textarea
                      name={`question_${q.id}`}
                      rows={4}
                      placeholder="Write your answer here..."
                      style={{
                        width: '100%',
                        borderRadius: '8px',
                        padding: '12px',
                        border: '1px solid #ccc',
                        fontFamily: 'inherit',
                        fontSize: '14px',
                        resize: 'vertical',
                      }}
                      value={userAnswers[q.id] || ''}
                      onChange={(e) =>
                        setUserAnswers((prev) => ({
                          ...prev,
                          [q.id]: e.target.value,
                        }))
                      }
                    />
                  )}
                </Box>
              ))}
            </List>
          ) : (
            <Typography variant="body2">No questions available.</Typography>
          )}

          <Box textAlign="right" mt={4}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              onClick={handleSubmitAnswers}
              sx={{ borderRadius: '8px', px: 4 }}
            >
              Submit Answers
            </Button>
          </Box>
        </>
      ) : (
        <Typography>No exam data available.</Typography>
      )}
    </Box>
  </Fade>
      </Modal>

    </Box>
  );
};

export default AutomaticExam;
